import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { useConfetti } from "../lib/confetti";
import { trackDonationSuccess } from "../lib/analytics";
import api from "../lib/api";
import { Heart, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function DonationSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling");
    const [tx, setTx] = useState(null);
    const { fire } = useConfetti();

    useEffect(() => {
        if (status === "paid") {
            // GA4 purchase event
            if (tx?.amount) trackDonationSuccess(tx.amount, sessionId);
            // Two bursts for extra celebration
            fire(window.innerWidth / 2, window.innerHeight / 2 - 100);
            setTimeout(() => fire(window.innerWidth * 0.25, window.innerHeight / 2), 350);
            setTimeout(() => fire(window.innerWidth * 0.75, window.innerHeight / 2), 700);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    useEffect(() => {
        if (!sessionId) { setStatus("error"); return; }
        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            if (attempts > 6) { setStatus("timeout"); return; }
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                setTx(data);
                if (data.payment_status === "paid") { setStatus("paid"); return; }
                if (data.status === "expired") { setStatus("expired"); return; }
                setTimeout(poll, 2000);
            } catch {
                setTimeout(poll, 2500);
            }
        };
        poll();
    }, [sessionId]);

    return (
        <div className="max-w-2xl mx-auto px-6 py-24">
            <Card className="card-surface p-10 rounded-2xl text-center">
                {status === "polling" && (
                    <>
                        <Loader2 className="h-10 w-10 text-primary mx-auto mb-4 animate-spin" />
                        <h1 className="font-display text-2xl text-accent">Confirming your donation…</h1>
                        <p className="text-muted-foreground text-sm mt-2">This only takes a few seconds.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center mx-auto mb-4 badge-glow-green">
                            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                        </div>
                        <h1 className="font-display text-3xl text-accent" data-testid="donation-success-heading">
                            Thank you.
                        </h1>
                        <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                            Your ${tx?.amount} donation is on its way to <span className="text-accent font-semibold">Hands of Hope</span>. You just made this mission possible.
                        </p>
                        <div className="mt-8 flex flex-wrap justify-center gap-3">
                            <Link to="/">
                                <Button variant="secondary" className="rounded-full">Back to fact-check</Button>
                            </Link>
                            <Link to="/transparency">
                                <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                                    <Heart className="h-4 w-4 mr-2" /> See impact
                                </Button>
                            </Link>
                        </div>
                        <div className="mt-10 pt-8 border-t border-white/10">
                            <p className="text-sm text-muted-foreground mb-4">
                                Want to keep helping without spending another dollar? Get the daily Fact of the Day —
                                every open plants a seed of truth.
                            </p>
                            <NewsletterSignup source="donation-success" compact />
                        </div>
                    </>
                )}
                {(status === "error" || status === "expired" || status === "timeout") && (
                    <>
                        <XCircle className="h-10 w-10 text-red-400 mx-auto mb-4" />
                        <h1 className="font-display text-2xl text-accent">Payment {status}</h1>
                        <p className="text-muted-foreground text-sm mt-2">
                            No charge was made. Please try again.
                        </p>
                        <Link to="/donate">
                            <Button className="mt-6 rounded-full bg-primary text-primary-foreground">Return to donate</Button>
                        </Link>
                    </>
                )}
            </Card>
        </div>
    );
}
